import React from 'react';
import { range } from '../util';

interface Props {
  rayCount: number;
  rayColorCenter: string;
  rayColorOuter: string;
  style?: React.CSSProperties;
  cx?: number;
  cy?: number;
  id?: string;
}

export default function Sunburst({
  rayCount,
  rayColorCenter,
  rayColorOuter,
  style,
  cx = 0.5,
  cy = 0.5,
  id,
}: Props) {
  const cxAbs = 1000 * cx;
  const cyAbs = 1000 * cy;
  const maxX = Math.max(cxAbs, 1000 - cxAbs);
  const maxY = Math.max(cyAbs, 1000 - cyAbs);
  const longestRay = (((maxX ** 2) + (maxY ** 2)) ** 0.5) * 1.1;
  const radianIncrement = Math.PI * 2 / rayCount;
  const pathData = [
    `M ${cxAbs} ${cyAbs}`,
    ...range(rayCount)
      .map(n => {
        const angle1 = radianIncrement * n;
        const angle2 = angle1 + radianIncrement / 2;
        const [x1, y1] = fromPolar(angle1, 100000, cxAbs, cyAbs);
        const [x2, y2] = fromPolar(angle2, 100000, cxAbs, cyAbs);
        return [`L ${x1} ${y1}`, `L ${x2} ${y2}`, 'Z'];
      })
      .flat()
  ].join(' ');
  return (
    <svg viewBox="0 0 1000 1000" style={style} id={id} width="1000" height="1000">
      <defs>
        <radialGradient gradientUnits="userSpaceOnUse" id="ray-gradient" cx={cxAbs} cy={cyAbs} fx={cxAbs} fy={cyAbs} r={longestRay}>
          <stop offset="0%" stopColor={rayColorCenter}/>
          <stop offset="100%" stopColor={rayColorOuter}/>
        </radialGradient>
      </defs>
      <path
        d={pathData}
        fill="url(#ray-gradient)"
      />
    </svg>
  );
}

function fromPolar(
  radians: number,
  len: number,
  offsetX: number,
  offsetY: number,
): [number, number] {
  const x = Math.sin(radians) * len;
  const y = Math.cos(radians) * len;
  return [x + offsetX, y + offsetY];
}
